import React from 'react';

interface CaptionWord {
  text: string;
  start: number;
  end: number;
}

interface Caption {
  id: string;
  text: string;
  start_time: number;
  end_time: number;
  style: Record<string, any>;
  words?: CaptionWord[];
}

interface KineticCaptionOverlayProps {
  captions: Caption[];
  currentTime: number;
  preset: string;
  fontFamily: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  textColor: string;
  highlightColor: string;
}

const wordsOf = (cap: Caption): CaptionWord[] =>
  cap.words && cap.words.length > 0 ? cap.words : [{ text: cap.text, start: cap.start_time, end: cap.end_time }];

const wordProgress = (word: CaptionWord, time: number): number => {
  const span = word.end - word.start;
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (time - word.start) / span));
};

const KineticCaptionOverlay: React.FC<KineticCaptionOverlayProps> = ({
  captions, currentTime, preset, fontFamily, fontSize, bold, italic, underline, textColor, highlightColor,
}) => {
  const active = captions.find(c => currentTime >= c.start_time && currentTime <= c.end_time) || null;
  const words = active ? wordsOf(active) : [];
  let activeIdx = -1;
  for (let i = 0; i < words.length; i++) {
    if (words[i].start <= currentTime) activeIdx = i; else break;
  }

  const baseStyle: React.CSSProperties = {
    fontFamily, fontSize, fontWeight: bold ? 800 : 500,
    fontStyle: italic ? 'italic' : 'normal',
    textDecoration: underline ? 'underline' : 'none',
    color: textColor,
  };

  if (!active || words.length === 0 || activeIdx < 0) {
    return <div className="kc-caption-overlay" />;
  }

  switch (preset) {
    case 'word-pop':
      return (
        <div className="kc-caption-overlay">
          <span key={`${active.id}-${activeIdx}`} className="kc-word-solo kc-anim-pop" style={baseStyle}>
            {words[activeIdx].text}
          </span>
        </div>
      );

    case 'bounce-in':
      return (
        <div className="kc-caption-overlay">
          <span key={`${active.id}-${activeIdx}`} className="kc-word-solo kc-anim-bounce" style={baseStyle}>
            {words[activeIdx].text}
          </span>
        </div>
      );

    case 'karaoke-box':
    case 'karaoke-color':
    case 'karaoke-scale':
      return (
        <div className="kc-caption-overlay">
          {words.map((w, i) => {
            const isActive = i === activeIdx;
            const style: React.CSSProperties = { ...baseStyle };
            if (preset === 'karaoke-box' && isActive) {
              style.background = highlightColor;
              style.color = '#03231d';
              style.padding = '0.07em 0.36em';
              style.borderRadius = '0.21em';
            } else if (preset === 'karaoke-color' && isActive) {
              style.color = highlightColor;
            } else if (preset === 'karaoke-scale' && isActive) {
              style.display = 'inline-block';
              style.transform = 'scale(1.25)';
              style.color = highlightColor;
            }
            return <span key={i} className="kc-caption-word kc-word-transition" style={style}>{w.text}</span>;
          })}
        </div>
      );

    case 'build-up':
      return (
        <div className="kc-caption-overlay">
          {words.slice(0, activeIdx + 1).map((w, i) => (
            <span key={`${active.id}-${i}`} className="kc-caption-word kc-word-fade" style={baseStyle}>{w.text}</span>
          ))}
        </div>
      );

    case 'underline-marker':
      return (
        <div className="kc-caption-overlay">
          {words.map((w, i) => (
            <span
              key={i}
              className="kc-caption-word kc-word-underline"
              style={{ ...baseStyle, borderBottomColor: i === activeIdx ? highlightColor : 'transparent' }}
            >
              {w.text}
            </span>
          ))}
        </div>
      );

    case 'fill-sweep': {
      const pct = (((activeIdx) + wordProgress(words[activeIdx], currentTime)) / words.length) * 100;
      return (
        <div className="kc-caption-overlay">
          <span
            className="kc-word-sweep"
            style={{
              ...baseStyle,
              color: undefined,
              ['--kc-sweep' as any]: `${pct}%`,
              ['--kc-sweep-color' as any]: highlightColor,
              ['--kc-base-color' as any]: textColor,
            }}
          >
            {words.map(w => w.text).join(' ')}
          </span>
        </div>
      );
    }

    case 'typewriter': {
      const full = words.map(w => w.text).join(' ');
      const priorChars = words.slice(0, activeIdx).map(w => w.text).join(' ').length + (activeIdx > 0 ? 1 : 0);
      const chars = priorChars + Math.ceil(words[activeIdx].text.length * wordProgress(words[activeIdx], currentTime));
      return (
        <div className="kc-caption-overlay">
          <span className="kc-word-type-wrap" style={{ fontSize }}>
            <span className="kc-word-type" style={baseStyle}>{full.slice(0, Math.max(0, chars))}</span>
            <span className="kc-word-caret" style={{ background: highlightColor }} />
          </span>
        </div>
      );
    }

    default:
      return (
        <div className="kc-caption-overlay">
          {words.map((w, i) => <span key={i} className="kc-caption-word" style={baseStyle}>{w.text}</span>)}
        </div>
      );
  }
};

export default KineticCaptionOverlay;
